import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import { formatPercentage } from '@helpers/utils/numbers/numbers';
import { THEME_TAG_COLOR_CLASS_MAP } from '@terminal/modules/reflexivity-graph/kg-insight-constants';
import { ThemeLogoIcon } from '@terminal/views/theme-overview/components/theme-logo-icon/ThemeLogoIcon';
import type { EarningsTopic } from '@terminal/views/theme-overview/tab-content/theme-earnings/earnings-reporting-summary/types';
import {
  type KeyStat,
  type SurpriseStat,
  toGuidanceTableKeyStat,
  toMedianKeyStat,
  toSurpriseKeyStat,
} from '@terminal/views/theme-overview/tab-content/theme-earnings/theme-key-stats/utils';
import type { GuidanceStat } from '@terminal/views/theme-overview/tab-content/theme-earnings/utils';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './IndustryThemesTable.module.scss';
import { KeyStatsCell } from './KeyStatsCell';
import { Placeholder } from './top-competitors/TopCompetitorCells';

export type IndustryThemeRowData = {
  themeId: string;
  rank: number;
  name: string;
  logoUrl?: string;
  reportedCount: number;
  totalCount: number;
  epsStat?: SurpriseStat;
  epsBeatCount: number;
  epsSurpriseCount: number;
  revenueStat?: SurpriseStat;
  revenueBeatCount: number;
  revenueSurpriseCount: number;
  guidanceStat?: GuidanceStat;
  medianT1?: number;
  medianT2?: number;
  leadingDriver?: EarningsTopic;
  isLoading: boolean;
};

export type IndustryThemesTableColumn = {
  id: string;
  header: string;
  alignment?: 'left' | 'right';
  cell: (row: IndustryThemeRowData) => ReactNode;
};

export const useIndustryThemesColumns = (): IndustryThemesTableColumn[] => {
  const { t } = useTranslation(['earnings']);

  const countOfTotalLabel = (count: number, total: number) =>
    t('earnings:peerComparison.industryThemes.countOfTotal', { count, total });

  const renderSurpriseCell = ({
    keyStat,
    beatCount,
    surpriseCount,
    beat,
  }: {
    keyStat: KeyStat;
    beatCount: number;
    surpriseCount: number;
    beat?: boolean;
  }) => {
    const cell = <KeyStatsCell keyStat={keyStat} primaryOnly />;

    if (surpriseCount === 0 || beat === undefined) {
      return cell;
    }

    return (
      <Tooltip
        label={countOfTotalLabel(
          beat ? beatCount : surpriseCount - beatCount,
          surpriseCount
        )}
        trigger={TooltipTrigger.Hover}
        inPortal
      >
        <span>{cell}</span>
      </Tooltip>
    );
  };

  return [
    {
      id: 'rank',
      header: t('earnings:peerComparison.industryThemes.rank'),
      cell: (row: IndustryThemeRowData) => <TopRankIcon rank={row.rank} />,
    },
    {
      id: 'theme',
      header: t('earnings:peerComparison.industryThemes.theme'),
      cell: (row: IndustryThemeRowData) => (
        <span className={styles.themeCell}>
          <ThemeLogoIcon
            name={row.name}
            logo_url={row.logoUrl}
            type="industry"
            size={20}
            fallbackIcon={
              <div className={styles.themeLogoFallback}>
                {row.name.charAt(0)}
              </div>
            }
          />
          <span className={styles.themeName}>{row.name}</span>
        </span>
      ),
    },
    {
      id: 'companiesReported',
      header: t('earnings:peerComparison.industryThemes.companiesReported'),
      alignment: 'right',
      cell: (row: IndustryThemeRowData) => {
        if (row.totalCount === 0) {
          return <Placeholder />;
        }

        return (
          <Tooltip
            label={t(
              'earnings:peerComparison.industryThemes.companiesReportedCount',
              {
                reported: row.reportedCount,
                total: row.totalCount,
              }
            )}
            trigger={TooltipTrigger.Hover}
            inPortal
          >
            <span className={styles.companiesReported}>
              {formatPercentage(row.reportedCount / row.totalCount, {
                decimals: 0,
                suffix: '%',
              })}
            </span>
          </Tooltip>
        );
      },
    },
    {
      id: 'eps',
      header: t('earnings:peerComparison.industryThemes.eps'),
      alignment: 'right',
      cell: (row: IndustryThemeRowData) =>
        renderSurpriseCell({
          keyStat: toSurpriseKeyStat('eps', row.epsStat, 0),
          beatCount: row.epsBeatCount,
          surpriseCount: row.epsSurpriseCount,
          beat: row.epsStat?.beat,
        }),
    },
    {
      id: 'revenue',
      header: t('earnings:peerComparison.industryThemes.revenue'),
      alignment: 'right',
      cell: (row: IndustryThemeRowData) =>
        renderSurpriseCell({
          keyStat: toSurpriseKeyStat('revenue', row.revenueStat, 0),
          beatCount: row.revenueBeatCount,
          surpriseCount: row.revenueSurpriseCount,
          beat: row.revenueStat?.beat,
        }),
    },
    {
      id: 'guidance',
      header: t('earnings:peerComparison.industryThemes.guidance'),
      alignment: 'left',
      cell: (row: IndustryThemeRowData) => (
        <span className={styles.keyStatsMuted}>
          <KeyStatsCell keyStat={toGuidanceTableKeyStat(row.guidanceStat)} />
        </span>
      ),
    },
    {
      id: 'peersMedianT1',
      header: t('earnings:peerComparison.industryThemes.peersMedianT1'),
      alignment: 'right',
      cell: (row: IndustryThemeRowData) => (
        <KeyStatsCell
          keyStat={toMedianKeyStat('peersMedianT1', row.medianT1)}
        />
      ),
    },
    {
      id: 'peersMedianT2',
      header: t('earnings:peerComparison.industryThemes.peersMedianT2'),
      alignment: 'right',
      cell: (row: IndustryThemeRowData) => (
        <KeyStatsCell
          keyStat={toMedianKeyStat('peersMedianT2', row.medianT2)}
        />
      ),
    },
    {
      id: 'leadingThemeDriver',
      header: t('earnings:peerComparison.industryThemes.leadingThemeDriver'),
      alignment: 'right',
      cell: (row: IndustryThemeRowData) =>
        row.leadingDriver?.label ? (
          <span
            className={THEME_TAG_COLOR_CLASS_MAP[row.leadingDriver.themeType]}
          >
            {row.leadingDriver.label}
          </span>
        ) : (
          <Placeholder />
        ),
    },
  ];
};
